import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPlus,
  faEdit,
  faTrash,
  faToggleOn,
  faToggleOff,
  faSearch,
  faFilter,
  faArrowLeft,
  faBuilding,
  faPhone,
  faEnvelope,
  faGlobe,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import './accounting.css';

const Banks = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [currentPage, setCurrentPage] = useState('list'); // 'list', 'add', 'edit'
  const [selectedBank, setSelectedBank] = useState(null);
  
  // نموذج إضافة/تعديل البنك
  const [formData, setFormData] = useState({
    bank_code: '',
    bank_name: '',
    swift_code: '',
    iban_prefix: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    logo: '',
    is_active: true
  });

  useEffect(() => {
    if (currentPage === 'list') {
      fetchBanks();
      fetchStatistics();
    }
  }, [currentPage]);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/banks', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      if (result.status) {
        setBanks(result.data.data || []);
        setError(null);
      } else {
        console.error('Failed to fetch banks:', result.message);
        setError(result.message);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/banks/statistics', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      if (result.status) {
        setStatistics(result.data);
      } else {
        console.error('Failed to fetch statistics:', result.message);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleToggleStatus = async (bankId) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/banks/${bankId}/toggle-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        fetchBanks();
        fetchStatistics();
      } else {
        setError(result.message || 'حدث خطأ في تغيير الحالة');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      setError(error.message);
    }
  };

  const handleDelete = async (bankId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا البنك؟')) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/banks/${bankId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
          },
        });

        const result = await response.json();
        if (result.status) {
          fetchBanks();
          fetchStatistics();
        } else {
          setError(result.message || 'حدث خطأ في حذف البنك');
        }
      } catch (error) {
        console.error('Error deleting bank:', error);
        setError(error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = currentPage === 'edit' 
        ? `https://naql.nozzm.com/api/banks/${selectedBank.id}`
        : 'https://naql.nozzm.com/api/banks';
      
      const method = currentPage === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.status) {
        setCurrentPage('list');
        setFormData({
          bank_code: '',
          bank_name: '',
          swift_code: '',
          iban_prefix: '',
          contact_person: '',
          phone: '',
          email: '',
          address: '',
          website: '',
          logo: '',
          is_active: true
        });
        fetchBanks();
        fetchStatistics();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('حدث خطأ أثناء حفظ البيانات');
    }
  };

  const handleEdit = (bank) => {
    setSelectedBank(bank);
    setFormData({
      bank_code: bank.bank_code || '',
      bank_name: bank.bank_name || '',
      swift_code: bank.swift_code || '',
      iban_prefix: bank.iban_prefix || '',
      contact_person: bank.contact_person || '',
      phone: bank.phone || '',
      email: bank.email || '',
      address: bank.address || '',
      website: bank.website || '',
      logo: bank.logo || '',
      is_active: bank.is_active !== undefined ? bank.is_active : true
    });
    setCurrentPage('edit');
  };

  const filteredBanks = banks.filter(bank => {
    const matchesSearch = bank.bank_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bank.bank_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || bank.is_active === (statusFilter === 'active');
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (isActive) => {
    return (
      <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
        {isActive ? t('accounting.banks.status.active') : t('accounting.banks.status.inactive')}
      </span>
    );
  };

  // Render Add/Edit Page
  const renderAddEditPage = () => {
    return (
      <div className="container tables bg-white mt-5">
        <div className="page-header">
          <button className="back-btn" onClick={() => setCurrentPage('list')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            العودة للقائمة
          </button>
          <div className="page-title">
            <h3>{currentPage === 'edit' ? 'تعديل البنك' : '+ إضافة بنك جديد'}</h3>
            <p>{currentPage === 'edit' ? 'تعديل بيانات البنك' : 'إنشاء بنك جديد في النظام'}</p>
          </div>
        </div>

        <div className="form-container">
          {/* Left Column - Help Panels */}
          <div className="help-panels">
            {/* Help Panel */}
            <div className="help-panel">
              <div className="panel-header">
                <FontAwesomeIcon icon={faSearch} className="info-icon" />
                <h6>مساعدة</h6>
              </div>
              <div className="panel-content">
                <div className="help-item">
                  <strong>رمز البنك:</strong>
                  <p>رمز فريد للبنك (مثال: ALB, RJH)</p>
                </div>
                <div className="help-item">
                  <strong>رمز السويفت:</strong>
                  <p>رمز SWIFT/BIC الدولي للبنك</p>
                </div>
                <div className="help-item">
                  <strong>بادئة الآيبان:</strong>
                  <p>الرمز المميز للبلد في رقم الآيبان</p>
                </div>
                <div className="help-item">
                  <strong>الشخص المسؤول:</strong>
                  <p>اسم الشخص المسؤول عن التواصل مع البنك</p>
                </div>
              </div>
            </div>

            {/* Bank Information Guide */}
            <div className="guide-panel">
              <div className="panel-header">
                <FontAwesomeIcon icon={faBuilding} className="info-icon" />
                <h6>دليل معلومات البنك</h6>
              </div>
              <div className="panel-content">
                <div className="bank-info-guide">
                  <div className="guide-item">
                    <h6 className="guide-title">البيانات الأساسية</h6>
                    <p>اسم البنك ورمز البنك وبيانات الاتصال الأساسية</p>
                  </div>
                  <div className="guide-item">
                    <h6 className="guide-title">البيانات الدولية</h6>
                    <p>رمز السويفت وبادئة الآيبان للتعاملات الدولية</p>
                  </div>
                  <div className="guide-item">
                    <h6 className="guide-title">بيانات الاتصال</h6>
                    <p>رقم الهاتف والبريد الإلكتروني والموقع الإلكتروني</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="form-section">
            <form onSubmit={handleSubmit} className="bank-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">
                    رمز البنك
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.bank_code || ''}
                    onChange={(e) => setFormData({...formData, bank_code: e.target.value})}
                    placeholder="مثال: ALB"
                    required
                  />
                  <small className="form-note">
                    رمز فريد للبنك (3 أحرف)
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">
                    اسم البنك
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.bank_name || ''}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    placeholder="اسم البنك"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    رمز السويفت
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.swift_code || ''}
                    onChange={(e) => setFormData({...formData, swift_code: e.target.value})}
                    placeholder="مثال: ALBLSARI"
                  />
                  <small className="form-note">
                    رمز SWIFT/BIC الدولي للبنك
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    بادئة الآيبان
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.iban_prefix || ''}
                    onChange={(e) => setFormData({...formData, iban_prefix: e.target.value})}
                    placeholder="مثال: SA"
                  />
                  <small className="form-note">
                    الرمز المميز للبلد في رقم الآيبان
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    الشخص المسؤول
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.contact_person || ''}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    placeholder="اسم الشخص المسؤول"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    رقم الهاتف
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="رقم الهاتف"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="البريد الإلكتروني"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    العنوان
                  </label>
                  <textarea
                    className="form-control"
                    value={formData.address || ''}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="عنوان البنك"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    الموقع الإلكتروني
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    value={formData.website || ''}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://www.bank.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    الحالة
                  </label>
                  <div className="status-toggle">
                    <input
                      type="checkbox"
                      id="status-toggle"
                      checked={formData.is_active !== undefined ? formData.is_active : true}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <label htmlFor="status-toggle" className="toggle-label">
                      {formData.is_active ? 'نشط' : 'غير نشط'}
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save">
                  حفظ البنك
                </button>
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setCurrentPage('list')}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render List Page
  const renderListPage = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>جاري تحميل البنوك...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="container tables bg-white mt-5">
        <div className="tableTitle d-flex justify-content-between align-items-center">
          <h3>{t('accounting.banks.title')}</h3>
          <div className="AddServicesButton">
            <button onClick={() => setCurrentPage('add')}>
              <FontAwesomeIcon icon={faPlus} />
              {t('accounting.banks.addBank')}
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faBuilding} />
            </div>
            <div className="stat-content">
              <h5>إجمالي البنوك</h5>
              <span className="stat-number">{statistics?.total_banks || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faToggleOn} />
            </div>
            <div className="stat-content">
              <h5>البنوك النشطة</h5>
              <span className="stat-number">{statistics?.active_banks || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faToggleOff} />
            </div>
            <div className="stat-content">
              <h5>البنوك غير النشطة</h5>
              <span className="stat-number">{statistics?.inactive_banks || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div className="stat-content">
              <h5>إجمالي الحسابات</h5>
              <span className="stat-number">{statistics?.total_accounts || 0}</span>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="row">
            <div className="col-md-6">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="البحث في البنوك..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="filter-box">
                <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">جميع الحالات</option>
                  <option value="active">نشط</option>
                  <option value="inactive">غير نشط</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
              >
                مسح الفلاتر
              </button>
            </div>
          </div>
        </div>

        {/* Banks Table */}
        <div className="table-responsive">
          <table className="table borderless">
            <thead>
              <tr>
                <th scope="col">الرقم</th>
                <th scope="col">{t('accounting.banks.bankCode')}</th>
                <th scope="col">{t('accounting.banks.bankName')}</th>
                <th scope="col">{t('accounting.banks.swiftCode')}</th>
                <th scope="col">{t('accounting.banks.contactPerson')}</th>
                <th scope="col">{t('accounting.banks.phone')}</th>
                <th scope="col">{t('accounting.banks.status')}</th>
                <th scope="col">الإعدادات</th>
              </tr>
            </thead>
            <tbody>
              {filteredBanks.map((bank) => (
                <tr key={bank.id}>
                  <td>{bank.id}</td>
                  <td>
                    <strong>{bank.bank_code}</strong>
                  </td>
                  <td>
                    <div>
                      <strong>{bank.bank_name}</strong>
                      {bank.website && (
                        <small className="text-muted d-block">
                          <FontAwesomeIcon icon={faGlobe} /> {bank.website}
                        </small>
                      )}
                    </div>
                  </td>
                  <td>{bank.swift_code || '-'}</td>
                  <td>
                    <div>
                      {bank.contact_person || '-'}
                      {bank.email && (
                        <small className="text-muted d-block">
                          <FontAwesomeIcon icon={faEnvelope} /> {bank.email}
                        </small>
                      )}
                    </div>
                  </td>
                  <td>
                    {bank.phone ? (
                      <div>
                        <FontAwesomeIcon icon={faPhone} /> {bank.phone}
                      </div>
                    ) : '-'}
                  </td>
                  <td className={bank.is_active ? 'activeIcon' : 'inactive'}>
                    {getStatusBadge(bank.is_active)}
                  </td>
                  <td>
                    <div className="icons">
                      <button
                        className="editbutton"
                        onClick={() => handleEdit(bank)}
                        title="تعديل"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="editbutton"
                        onClick={() => handleToggleStatus(bank.id)}
                        title={bank.is_active ? 'إلغاء التفعيل' : 'تفعيل'}
                      >
                        <FontAwesomeIcon icon={bank.is_active ? faToggleOn : faToggleOff} />
                      </button>
                      <button
                        className="editbutton"
                        onClick={() => handleDelete(bank.id)}
                        title="حذف"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBanks.length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted">لا توجد بنوك لعرضها</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render based on current page
  if (currentPage === 'add' || currentPage === 'edit') {
    return renderAddEditPage();
  }

  return renderListPage();
};

export default Banks;
